/**
 * Classify a free-text search query for the customer-search Edge function.
 * Precedence: email > phone > VIN (exact) > VIN (partial) > plate > name.
 *
 * One implementation for every caller (useCustomerSearch, useCustomer) — the two private copies
 * had drifted into the same defect: any 6-16 character alphanumeric query was sent to the
 * VIN-suffix index, so a single-word surname ("Johnson", "Martinez") returned nothing.
 * A partial VIN always carries digits (the serial section is numeric); an all-letter query is a name.
 */
export type CustomerSearchType = 'email' | 'phone' | 'vin' | 'plate' | 'name';

export function detectSearchType(query: string): CustomerSearchType {
  if (query.includes('@')) return 'email';
  const digitsOnly = query.replace(/\D/g, '');
  if (digitsOnly.length >= 7 && digitsOnly.length === query.replace(/[\s\-().+]/g, '').length) return 'phone';
  const alphanumOnly = query.replace(/[\s\-]/g, '').toUpperCase();
  if (/^[A-HJ-NPR-Z0-9]{17}$/.test(alphanumOnly)) return 'vin';
  if (alphanumOnly.length >= 6 && alphanumOnly.length <= 16 && /^[A-Z0-9]+$/.test(alphanumOnly) && /[0-9]/.test(alphanumOnly)) return 'vin';
  if (alphanumOnly.length >= 2 && alphanumOnly.length <= 8 && /[A-Z]/.test(alphanumOnly) && /[0-9]/.test(alphanumOnly)) return 'plate';
  // Core-100 R4 CORE-AY: a short run of digits (3–6, too short to be a phone) is most often the end of a plate the
  // searcher half-remembers; customer-search tries it as a whole plate, then as a plate ending. (Ticket numbers are
  // found by the universal_search RPC, which runs beside it.)
  if (/^[0-9]{3,6}$/.test(alphanumOnly)) return 'plate';
  return 'name';
}
